import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import { Button, Col, Input, Label, Modal, ModalBody, ModalHeader, Row, Table } from "reactstrap";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";

const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const fmt = (v) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);

function ListadoAbonosProveedores() {
  const { user } = useContext(AuthContext);
  const URL_ABONOS = process.env.REACT_APP_URL_ABONOS_PROVEEDORES;
  const [pagos, setPagos] = useState([]);
  const [inicio, setInicio] = useState(moment().subtract(3, "months").format("YYYY-MM-DD"));
  const [fin, setFin] = useState(moment().format("YYYY-MM-DD"));
  const [buscar, setBuscar] = useState("");
  const [estado, setEstado] = useState("Si");
  const [detalle, setDetalle] = useState(null);

  const cargar = () => axios.get(`${URL_ABONOS}Fechas/${inicio}/${fin}`, { headers: headers() })
    .then((r) => setPagos(r.data.sort((a, b) => b.idAbonoProveedor - a.idAbonoProveedor)))
    .catch(console.log);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [inicio, fin]);

  const visibles = useMemo(() => {
    const q = buscar.toLowerCase();
    return pagos.filter((p) => (!estado || (p.is_active || "Si") === estado) && (!q || `${p.idAbonoProveedor} ${p.proveedores?.[0]?.nombre_comercial}`.toLowerCase().includes(q)));
  }, [pagos, buscar, estado]);
  const total = visibles.reduce((t, p) => t + p.importe, 0);

  const cancelar = async (p) => {
    const ok = await Swal.fire({ title: `¿Cancelar el pago ${p.idAbonoProveedor}?`, text: "El saldo regresa a cada compra y al proveedor.", icon: "warning", showCancelButton: true, confirmButtonText: "Si, cancelar", cancelButtonText: "No" });
    if (!ok.value) return;
    try {
      await axios.patch(`${URL_ABONOS}/cancelar/${p._id}`, {}, { headers: headers() });
      Swal.fire("Pago cancelado", "", "success");
      cargar();
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se cancelo", text: error.response?.data?.message || "Intenta de nuevo" });
    }
  };

  if (!user.pagoProveedores) return <Header />;

  return (
    <>
      <Header />
      <div className="card container col-12">
        <div className="page-header">
          <div><h3 className="page-title">Pagos a proveedores</h3><p className="page-subtitle">{visibles.length} pagos · {fmt(total)}</p></div>
          {user.pagoProveedoresCreate ? <a className="btn btn-sm btn-primary" href="/AbonosProveedoresCreate">Nuevo pago</a> : null}
        </div>
        <Row className="filter-row">
          <Col md={2}><Label>Desde</Label><Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} /></Col>
          <Col md={2}><Label>Hasta</Label><Input type="date" value={fin} onChange={(e) => setFin(e.target.value)} /></Col>
          <Col md={2}><Label>Estado</Label><Input type="select" value={estado} onChange={(e) => setEstado(e.target.value)}><option value="Si">Activos</option><option value="No">Cancelados</option><option value="">Todos</option></Input></Col>
          <Col md={4}><Label>Buscar</Label><Input value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Folio o proveedor" /></Col>
        </Row>
        <Table size="sm" responsive className="mt-3">
          <thead><tr><th>Folio</th><th>Fecha</th><th>Proveedor</th><th>Banco</th><th>Concepto</th><th className="text-right">Compras</th><th className="text-right">Importe</th><th>Estado</th><th /></tr></thead>
          <tbody>
            {visibles.map((p) => (
              <tr key={p._id}>
                <td>{p.idAbonoProveedor}</td><td>{p.fecha}</td><td>{p.proveedores?.[0]?.nombre_comercial}</td>
                <td>{p.bancos?.[0]?.banco}</td><td>{p.concepto}</td><td className="text-right">{(p.aplicaciones || []).length}</td>
                <td className="text-right td-money">{fmt(p.importe)}</td>
                <td><span className={`badge-soft-${(p.is_active || "Si") === "Si" ? "success" : "danger"}`}>{(p.is_active || "Si") === "Si" ? "Activo" : "Cancelado"}</span></td>
                <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                  <Button size="sm" color="secondary" title="Detalle" onClick={() => setDetalle(p)}><i className="fas fa-eye" /></Button>{" "}
                  {(p.is_active || "Si") === "Si" && user.pagos_proveedor_cancelar ? <Button size="sm" color="danger" title="Cancelar" onClick={() => cancelar(p)}><i className="fas fa-ban" /></Button> : null}
                </td>
              </tr>
            ))}
            {visibles.length === 0 && <tr><td colSpan={9} className="text-center">Sin pagos en el periodo</td></tr>}
          </tbody>
        </Table>
      </div>
      <Modal size="lg" isOpen={!!detalle} toggle={() => setDetalle(null)}>
        <ModalHeader toggle={() => setDetalle(null)}>Pago {detalle?.idAbonoProveedor}</ModalHeader>
        <ModalBody>
          <Table size="sm" responsive>
            <thead><tr><th>Compra</th><th>Fecha</th><th>OC</th><th className="text-right">Total compra</th><th className="text-right">Aplicado</th><th className="text-right">Saldo actual</th></tr></thead>
            <tbody>
              {(detalle?.aplicaciones || []).map((a) => (
                <tr key={a._id}>
                  <td>{a.notasCxP?.idNotasCxP}</td><td>{a.notasCxP?.fecha}</td><td>{a.notasCxP?.ordenesCompra?.[0]?.idOrdenCompra}</td>
                  <td className="text-right td-money">{fmt(a.notasCxP?.total_costo)}</td>
                  <td className="text-right td-money">{fmt(a.importe)}</td>
                  <td className="text-right td-money">{fmt(a.notasCxP?.saldo)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ModalBody>
      </Modal>
    </>
  );
}

export default ListadoAbonosProveedores;
